import { Stack } from '@mui/material'
import { ContentChip } from 'ComponentLibrary/src/piazza/content'
import { Typography } from 'ComponentLibrary/src/piazza'
import { decimalToLocalizedString, joinDefinedStrings, Percentage, tokens } from 'ComponentLibrary'
import { DeviationValueType } from './types'
import { getDeviationValueAndUnit } from './DeviationValue'
import { isNil } from 'lodash'
import { ArrowDownRightIcon, ArrowRightIcon, ArrowUpRightIcon } from '@phosphor-icons/react'

const getDeviationTrendStyles = (
    deviationChange?: number,
    invertTrendColors = false
): {
    chipColor: 'green' | 'red' | 'blue' | 'grey'
    textColor: string
    iconColor: string
} => {
    const effectiveDeviationChange = deviationChange === undefined ? undefined : invertTrendColors ? -deviationChange : deviationChange

    if (effectiveDeviationChange === undefined) {
        return {
            chipColor: 'grey',
            textColor: tokens.ColorTextSubtle,
            iconColor: tokens.ColorIconInformative,
        }
    }

    if (effectiveDeviationChange > 0) {
        return {
            chipColor: 'green',
            textColor: tokens.ColorTextPositiveSubtle,
            iconColor: tokens.ColorIconPositive,
        }
    }

    if (effectiveDeviationChange < 0) {
        return {
            chipColor: 'red',
            textColor: tokens.ColorTextDangerSubtle,
            iconColor: tokens.ColorIconDanger,
        }
    }

    return {
        chipColor: 'blue',
        textColor: tokens.ColorTextInformativeSubtle,
        iconColor: tokens.ColorIconInformative,
    }
}

const getDeviationChangeText = (value?: number, unit?: string, maximumNumberOfDigits = 2, locale = 'de-DE') => {
    let valueText
    if (!isNil(value)) {
        const sign = value < 0 ? '-' : value > 0 ? '+' : undefined
        const absoluteValue = Math.abs(value)
        const number = decimalToLocalizedString(absoluteValue, false, maximumNumberOfDigits, maximumNumberOfDigits, locale)
        valueText = joinDefinedStrings([sign, number])
    } else {
        valueText = '-'
    }
    return joinDefinedStrings([valueText, unit])
}

type DeviationChangeValueProps = {
    deviationChange?: DeviationValueType
    percentagePointUnit?: string
    locale?: string
    yearUnitLabels?: { one: string; other: string }
}

type DeviationChangePercentageProps = {
    deviationChange?: DeviationValueType
    deviationPercentage?: Percentage
    withoutChip?: boolean
    invertTrendColors?: boolean
    percentagePointUnit?: string
    locale?: string
    yearUnitLabels?: { one: string; other: string }
}

export function DeviationChangeValue({ deviationChange, percentagePointUnit, locale, yearUnitLabels }: DeviationChangeValueProps) {
    const valueAndUnit = getDeviationValueAndUnit(deviationChange, true, percentagePointUnit, locale, yearUnitLabels)

    return (
        <Typography variant="textS" color={tokens.ColorTextSubtle} noWrap>
            {getDeviationChangeText(valueAndUnit?.value, valueAndUnit?.unit, 2, locale)}
        </Typography>
    )
}

export function DeviationChangePercentage({
    deviationChange,
    deviationPercentage,
    withoutChip,
    invertTrendColors,
    percentagePointUnit,
    locale,
    yearUnitLabels,
}: DeviationChangePercentageProps) {
    const valueAndUnit = getDeviationValueAndUnit(deviationChange, true, percentagePointUnit, locale, yearUnitLabels)
    const hasDeviationPercentage = !isNil(deviationPercentage)
    const trendStyles = getDeviationTrendStyles(valueAndUnit?.value, invertTrendColors)
    let trendIcon = null

    if (valueAndUnit?.value !== undefined) {
        if (valueAndUnit.value > 0) {
            trendIcon = <ArrowUpRightIcon weight="bold" color={trendStyles.iconColor} size={tokens.SizingIconS} />
        } else if (valueAndUnit.value < 0) {
            trendIcon = <ArrowDownRightIcon weight="bold" color={trendStyles.iconColor} size={tokens.SizingIconS} />
        } else {
            trendIcon = <ArrowRightIcon weight="bold" color={trendStyles.iconColor} size={tokens.SizingIconS} />
        }
    }

    if (withoutChip) {
        if (!hasDeviationPercentage) {
            return null
        }
        return (
            <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                {trendIcon}
                <Typography variant="textS" color={trendStyles.textColor} noWrap>
                    {getDeviationChangeText(deviationPercentage?.toPercentage(), '%', 2, locale)}
                </Typography>
            </Stack>
        )
    }

    if (!hasDeviationPercentage) {
        return null
    }

    return (
        <ContentChip
            size="small"
            color={trendStyles.chipColor}
            label={getDeviationChangeText(deviationPercentage?.toPercentage(), '%', 2, locale)}
        />
    )
}
